/**
 * BulkInviteTab — Mass-import users via CSV upload or paste.
 *
 * Flow:
 *  1. User drops a .csv file or pastes Excel-style rows
 *  2. Rows are parsed client-side with papaparse / native split
 *  3. Each row is validated and shown in a preview table with status badges
 *  4. On "Confirmar e Enviar X Convites" → batch Promise.allSettled
 *     with a live progress bar; on finish, invalidates the users query
 */
import React, { useCallback, useRef, useState } from 'react';
import Papa from 'papaparse';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileUp,
  Loader2,
  Users,
  XCircle,
} from 'lucide-react';
import type { OrgDepartment, OrgRole } from '../../../services/organizationStructureService';
import { InvitationService } from '../../../services/invitationService';

// ─── Types ───────────────────────────────────────────────────────────────────

type RowStatus = 'valid' | 'invalid_email' | 'warn_dept' | 'warn_role' | 'warn_both';

interface BulkRow {
  /** Sequential index for stable keys */
  idx: number;
  email: string;
  roleName: string;
  deptName: string;
  resolvedDeptId: string | null;
  resolvedRoleId: string | null;
  status: RowStatus;
  /** Human-readable issues (empty = no issues) */
  issues: string[];
}

interface BulkInviteTabProps {
  organizationId: string;
  inviteRole: string;
  departments: OrgDepartment[];
  orgRoles: OrgRole[];
  onComplete: () => void;
  onCancel: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Fuzzy name match: lower + trim */
const normName = (s: string) => s.toLowerCase().trim();

function parseRawRows(
  raw: string,
  departments: OrgDepartment[],
  orgRoles: OrgRole[],
): BulkRow[] {
  if (!raw.trim()) return [];

  // Detect if it looks like CSV with commas or TSV (Excel paste = tabs)
  const hasTab = raw.includes('\t');

  const parsed = Papa.parse<string[]>(raw, {
    delimiter: hasTab ? '\t' : ',',
    skipEmptyLines: true,
  });

  const rows: BulkRow[] = [];

  parsed.data.forEach((cols, i) => {
    const emailRaw = (cols[0] ?? '').trim();
    const roleNameRaw = (cols[1] ?? '').trim();
    const deptNameRaw = (cols[2] ?? '').trim();

    // Skip header row
    if (i === 0 && /^e[- ]?mail$/i.test(emailRaw)) return;

    const email = emailRaw.toLowerCase();
    const roleName = roleNameRaw;
    const deptName = deptNameRaw;

    const issues: string[] = [];

    // Email validation
    if (!EMAIL_RE.test(email)) {
      issues.push('E-mail inválido');
    }

    // Department resolution
    let resolvedDeptId: string | null = null;
    if (deptName) {
      const matched = departments.find((d) => normName(d.name) === normName(deptName));
      if (matched) {
        resolvedDeptId = matched.id;
      } else {
        issues.push(`Departamento "${deptName}" não encontrado`);
      }
    }

    // Role resolution
    let resolvedRoleId: string | null = null;
    if (roleName) {
      const matched = orgRoles.find((r) => normName(r.name) === normName(roleName));
      if (matched) {
        resolvedRoleId = matched.id;
      } else {
        issues.push(`Cargo "${roleName}" não encontrado`);
      }
    }

    // Compute status
    let status: RowStatus;
    const emailInvalid = !EMAIL_RE.test(email);
    const hasWarnings = issues.length > (emailInvalid ? 1 : 0);

    if (emailInvalid) {
      status = 'invalid_email';
    } else if (deptName && !resolvedDeptId && roleName && !resolvedRoleId) {
      status = 'warn_both';
    } else if (deptName && !resolvedDeptId) {
      status = 'warn_dept';
    } else if (roleName && !resolvedRoleId) {
      status = 'warn_role';
    } else {
      status = 'valid';
    }

    rows.push({ idx: i, email, roleName, deptName, resolvedDeptId, resolvedRoleId, status, issues });
  });

  return rows;
}

const STATUS_BADGE: Record<RowStatus, React.ReactNode> = {
  valid: (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
      <CheckCircle2 size={9} /> Válido
    </span>
  ),
  invalid_email: (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300">
      <XCircle size={9} /> E-mail inválido
    </span>
  ),
  warn_dept: (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
      <AlertTriangle size={9} /> Depto. não encontrado
    </span>
  ),
  warn_role: (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
      <AlertTriangle size={9} /> Cargo não encontrado
    </span>
  ),
  warn_both: (
    <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
      <AlertTriangle size={9} /> Depto. e cargo não encontrados
    </span>
  ),
};

/** Download a UTF-8 CSV to the user's machine without any external deps */
function downloadTemplate() {
  const csv = [
    'email,cargo,departamento',
    'joao.silva@empresa.com,Analista de Processos,Engenharia',
    'maria.souza@empresa.com,Gerente de Projetos,Operações',
  ].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'template_convites.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Component ───────────────────────────────────────────────────────────────

export const BulkInviteTab: React.FC<BulkInviteTabProps> = ({
  organizationId,
  inviteRole,
  departments,
  orgRoles,
  onComplete,
  onCancel,
}) => {
  const [pasteText, setPasteText] = useState('');
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [progress, setProgress] = useState(0);   // 0-100
  const [sentCount, setSentCount] = useState(0);
  const [failedCount, setFailedCount] = useState(0);
  const [done, setDone] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const processText = useCallback(
    (text: string) => {
      setPasteText(text);
      const parsed = parseRawRows(text, departments, orgRoles);
      setRows(parsed);
      setDone(false);
      setProgress(0);
      setSentCount(0);
      setFailedCount(0);
    },
    [departments, orgRoles],
  );

  // ── File drop ────────────────────────────────────────────────────────────
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => processText(ev.target?.result as string ?? '');
      reader.readAsText(file, 'UTF-8');
    },
    [processText],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = (ev) => processText(ev.target?.result as string ?? '');
      reader.readAsText(file, 'UTF-8');
      e.target.value = '';
    },
    [processText],
  );

  // ── Batch send ───────────────────────────────────────────────────────────
  const sendableRows = rows.filter((r) => r.status !== 'invalid_email');

  const handleSend = async () => {
    if (!sendableRows.length) return;
    setSending(true);
    setProgress(0);
    setSentCount(0);
    setFailedCount(0);

    const total = sendableRows.length;
    let sent = 0;
    let failed = 0;

    // Sequential to avoid rate-limit / DB contention
    for (const row of sendableRows) {
      try {
        await InvitationService.create({
          organization_id: organizationId,
          email: row.email,
          role: inviteRole,
          department_id: row.resolvedDeptId,
          org_role_id: row.resolvedRoleId,
        });
        sent++;
      } catch {
        failed++;
      }
      setSentCount(sent);
      setFailedCount(failed);
      setProgress(Math.round(((sent + failed) / total) * 100));
    }

    setSending(false);
    setDone(true);

    if (sent > 0) {
      // Small delay so the user sees the 100% bar
      setTimeout(onComplete, 800);
    }
  };

  const validCount = rows.filter((r) => r.status === 'valid').length;
  const warnCount = rows.filter((r) => r.status !== 'invalid_email' && r.status !== 'valid').length;
  const errorCount = rows.filter((r) => r.status === 'invalid_email').length;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col gap-3">
      {/* Template download */}
      <div className="flex items-center justify-between">
        <span className="text-[11px] enterprise-text-secondary">
          Formato: <code className="font-mono">email, cargo, departamento</code>
        </span>
        <button
          type="button"
          onClick={downloadTemplate}
          className="flex items-center gap-1 text-[11px] text-blue-500 hover:text-blue-600 hover:underline"
        >
          <Download size={11} /> Baixar Template
        </button>
      </div>

      {/* Dropzone */}
      <div
        className={`relative flex flex-col items-center justify-center gap-2 rounded border-2 border-dashed p-5 transition-colors cursor-pointer
          ${isDragOver
            ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
            : 'enterprise-border bg-[var(--bg-elevated)] hover:border-slate-400 dark:hover:border-slate-500'
          }`}
        onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
      >
        <FileUp size={20} className="enterprise-text-secondary" />
        <p className="text-[12px] enterprise-text-secondary text-center">
          Arraste um arquivo <strong>.csv</strong> aqui ou <span className="text-blue-500">clique para selecionar</span>
        </p>
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {/* Paste textarea */}
      <div>
        <label className="block text-[11px] font-medium enterprise-text-secondary mb-1">
          Ou cole as linhas do Excel aqui <span className="font-normal">(Formato: Email, Cargo, Departamento)</span>
        </label>
        <textarea
          className="enterprise-input w-full text-[12px] font-mono resize-none"
          rows={4}
          placeholder={"joao@empresa.com,Analista,Engenharia\nmaria@empresa.com,Gerente,Operações"}
          value={pasteText}
          onChange={(e) => processText(e.target.value)}
          spellCheck={false}
        />
      </div>

      {/* Preview table */}
      {rows.length > 0 && (
        <div>
          {/* Stats strip */}
          <div className="flex items-center gap-3 mb-2">
            <span className="flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 size={11} /> {validCount} válido{validCount !== 1 ? 's' : ''}
            </span>
            {warnCount > 0 && (
              <span className="flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
                <AlertTriangle size={11} /> {warnCount} aviso{warnCount !== 1 ? 's' : ''}
              </span>
            )}
            {errorCount > 0 && (
              <span className="flex items-center gap-1 text-[11px] text-red-500">
                <XCircle size={11} /> {errorCount} inválido{errorCount !== 1 ? 's' : ''}
              </span>
            )}
            <span className="ml-auto text-[11px] enterprise-text-secondary">
              {sendableRows.length} serão enviados
            </span>
          </div>

          {/* Scrollable preview table */}
          <div className="max-h-[200px] overflow-y-auto rounded border enterprise-border">
            <table className="w-full text-left text-[11px] table-fixed">
              <colgroup>
                <col className="w-[180px]" />
                <col className="w-[110px]" />
                <col className="w-[100px]" />
                <col />
              </colgroup>
              <thead className="sticky top-0 enterprise-table-header">
                <tr>
                  <th className="enterprise-table-head-cell">E-mail</th>
                  <th className="enterprise-table-head-cell">Cargo</th>
                  <th className="enterprise-table-head-cell">Departamento</th>
                  <th className="enterprise-table-head-cell">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr
                    key={row.idx}
                    className={`enterprise-table-row ${row.status === 'invalid_email' ? 'opacity-50' : ''}`}
                  >
                    <td className="enterprise-table-cell font-mono truncate" title={row.email}>
                      {row.email || <span className="italic enterprise-text-secondary">—</span>}
                    </td>
                    <td className="enterprise-table-cell truncate" title={row.roleName}>
                      {row.roleName
                        ? row.resolvedRoleId
                          ? <span className="text-emerald-600 dark:text-emerald-400">{row.roleName}</span>
                          : <span className="text-amber-600">{row.roleName}</span>
                        : <span className="italic enterprise-text-secondary">—</span>}
                    </td>
                    <td className="enterprise-table-cell truncate" title={row.deptName}>
                      {row.deptName
                        ? row.resolvedDeptId
                          ? <span className="text-emerald-600 dark:text-emerald-400">{row.deptName}</span>
                          : <span className="text-amber-600">{row.deptName}</span>
                        : <span className="italic enterprise-text-secondary">—</span>}
                    </td>
                    <td className="enterprise-table-cell">{STATUS_BADGE[row.status]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Progress bar */}
      {(sending || done) && (
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-[11px] enterprise-text-secondary">
              {done
                ? `Concluído — ${sentCount} enviado${sentCount !== 1 ? 's' : ''}${failedCount > 0 ? `, ${failedCount} falha${failedCount !== 1 ? 's' : ''}` : ''}`
                : `Enviando… ${sentCount + failedCount} / ${sendableRows.length}`}
            </span>
            <span className="text-[11px] tabular-nums enterprise-text-secondary">{progress}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-300 ${done && failedCount === 0 ? 'bg-emerald-500' : done ? 'bg-amber-500' : 'bg-blue-500'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Empty state */}
      {rows.length === 0 && !pasteText && (
        <div className="flex items-center gap-2 rounded border enterprise-border px-3 py-2 bg-[var(--bg-elevated)]">
          <Users size={13} className="enterprise-text-secondary shrink-0" />
          <span className="text-[11px] enterprise-text-secondary">
            Carregue um arquivo ou cole os dados para visualizar o preview.
          </span>
        </div>
      )}

      {/* Footer actions */}
      <div className="flex items-center justify-between mt-1">
        <button
          type="button"
          className="enterprise-button-secondary px-3 py-1.5 text-xs"
          onClick={onCancel}
          disabled={sending}
        >
          Cancelar
        </button>

        <button
          type="button"
          className="enterprise-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5 disabled:opacity-50"
          onClick={handleSend}
          disabled={sending || sendableRows.length === 0 || done}
        >
          {sending
            ? <><Loader2 size={13} className="animate-spin" /> Enviando…</>
            : <><Users size={13} /> Confirmar e Enviar {sendableRows.length} Convite{sendableRows.length !== 1 ? 's' : ''}</>}
        </button>
      </div>
    </div>
  );
};
